import { useCallback, useMemo, useRef, useState } from "react";
import {
  useGetProductsQuery,
  useGetBrandsQuery,
  useGetSuppliersQuery,
  useGetCategoriesQuery,
  useGetWarehousesQuery,
  useGetStockLevelsQuery,
  useGetOrdersQuery,
  useGetUsersQuery,
  useBulkUpdateProductsMutation,
  useBulkDeleteProductsMutation,
} from "../../../../redux/api";
import type { Product, UpdateProductInput } from "../../../../redux/api";
import { IconPlus, IconEye, IconEdit, IconTrash, IconSearch, IconImage, IconBox, IconAlert, IconChart } from "../../../../components/icons";
import { RowMenu } from "../../../../components/RowMenu";
import { StatCard } from "../../../../components/StatCard";
import { useProductTotals } from "./productTotals";
import { ProductFormModal } from "./product";
import { Modal } from "../../../../components/Modal";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type SelectionChangedEvent,
  type ICellRendererParams,
  type ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useAppTheme } from "../../../../theme/PreferencesContext";

// ag-grid v33+ requires modules to be registered explicitly (once).
ModuleRegistry.registerModules([AllCommunityModule]);

// Map the grid theme onto the app's design tokens. ag-grid resolves these into
// its own CSS variables at theme-build time and does not re-read them, so the
// values can't be `var(--surface)` and so on — the two palettes are spelled out
// here and picked between at render (see gridThemeFor). Keep them in step with
// the light/dark blocks in styles/style.css.
const gridThemeBase = {
  fontFamily: "Poppins, sans-serif",
  headerFontWeight: 600,
  wrapperBorderRadius: 12,
};

const lightGridTheme = themeQuartz.withParams({
  ...gridThemeBase,
  accentColor: "#2563eb",
  browserColorScheme: "light",
  borderColor: "#e5e7eb",
  headerBackgroundColor: "#f8fafc",
  rowHoverColor: "#f1f5f9",
  selectedRowBackgroundColor: "#e0e7ff",
});

const darkGridTheme = themeQuartz.withParams({
  ...gridThemeBase,
  accentColor: "#3b82f6",
  browserColorScheme: "dark",
  backgroundColor: "#111827",
  foregroundColor: "#f3f4f6",
  borderColor: "#1f2937",
  headerBackgroundColor: "#0f1729",
  rowHoverColor: "#1b2436",
  selectedRowBackgroundColor: "#1e293b",
});

const gridThemeFor = (isDarkMode: boolean) =>
  isDarkMode ? darkGridTheme : lightGridTheme;

const STATUS_TONE: Record<string, string> = {
  ACTIVE: "badge--success",
  INACTIVE: "badge--warning",
  DISCONTINUED: "badge--danger",
  DRAFT: "badge--info",
};

const statusTone = (status?: string | null) =>
  STATUS_TONE[(status ?? "").toUpperCase()] ?? "badge--info";

const money = (value?: number | null, currency?: string) =>
  typeof value === "number"
    ? `${currency ? `${currency} ` : ""}${value.toLocaleString()}`
    : "—";

const isLowStock = (p?: Product | null) =>
  !!p &&
  p.inventoryTracking !== "NONE" &&
  (p.totalStock ?? 0) <= (p.reorderLevel ?? 0);

// Fields the bulk editor can set in one shot — scoped to exactly what
// bulkUpdateProducts supports server-side, grouped into the tabs the flyout
// shows (General/Prices/Stock). No Images/Shipping/Advanced tabs — there's
// nothing on the backend to wire those to.
type BulkFieldKey =
  | "status"
  | "inventoryTracking"
  | "currency"
  | "categoryId"
  | "brandId"
  | "supplierId"
  | "sellingPrice"
  | "purchasePrice"
  | "reorderLevel"
  | "isFeatured";

type BulkTab = "general" | "prices" | "stock";

const BULK_FIELDS: Record<BulkFieldKey, { label: string; tab: BulkTab }> = {
  status: { label: "Status", tab: "general" },
  isFeatured: { label: "Featured", tab: "general" },
  categoryId: { label: "Category", tab: "general" },
  brandId: { label: "Brand", tab: "general" },
  supplierId: { label: "Supplier", tab: "general" },
  sellingPrice: { label: "Selling price", tab: "prices" },
  purchasePrice: { label: "Purchase price", tab: "prices" },
  currency: { label: "Currency", tab: "prices" },
  inventoryTracking: { label: "Inventory tracking", tab: "stock" },
  reorderLevel: { label: "Reorder level", tab: "stock" },
};

const BULK_TABS: { key: BulkTab; label: string }[] = [
  { key: "general", label: "General" },
  { key: "prices", label: "Prices" },
  { key: "stock", label: "Stock" },
];

// Inventory → Products (ag-grid list with row selection, bulk edit/delete,
// filtering, CSV export and low-stock highlighting). Create/edit happen in a
// popup (ProductFormModal, from product.tsx).
const Products = () => {
  const {
    data: products = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetProductsQuery();
  const { data: brands = [] } = useGetBrandsQuery();
  const { data: suppliers = [] } = useGetSuppliersQuery();
  const { data: categories = [] } = useGetCategoriesQuery();
  const { data: users = [] } = useGetUsersQuery();

  const [bulkUpdateProducts, { isLoading: bulkUpdating }] =
    useBulkUpdateProductsMutation();
  const [bulkDeleteProducts, { isLoading: bulkDeleting }] =
    useBulkDeleteProductsMutation();

  const totals = useProductTotals(products);

  // ag-grid builds its palette from JS, not from the stylesheet's custom
  // properties, so this is one of the few places that needs the flag rather
  // than a CSS token.
  const { isDarkMode } = useAppTheme();

  const gridApiRef = useRef<GridApi<Product> | null>(null);
  const [selected, setSelectedRows] = useState<Product[]>([]);
  const [search, setSearch] = useState("");

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTab, setBulkTab] = useState<BulkTab>("general");
  const [bulkEnabled, setBulkEnabled] = useState<Partial<Record<BulkFieldKey, boolean>>>({});
  const [bulkValues, setBulkValues] = useState<Partial<Record<BulkFieldKey, string>>>({});

  const setBulkFieldMode = (field: BulkFieldKey, mode: "none" | "set") =>
    setBulkEnabled((prev) => ({ ...prev, [field]: mode === "set" }));
  const setBulkFieldValue = (field: BulkFieldKey, value: string) =>
    setBulkValues((prev) => ({ ...prev, [field]: value }));

  // id → display-name lookups for the reference columns.
  const brandName = useMemo(
    () => Object.fromEntries(brands.map((b) => [b.id, b.name])),
    [brands],
  );
  const supplierName = useMemo(
    () => Object.fromEntries(suppliers.map((s) => [s.id, s.name])),
    [suppliers],
  );
  const categoryName = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.name])),
    [categories],
  );
  const nameByEmail = useMemo(
    () =>
      Object.fromEntries(
        users.map((u) => [
          u.email.toLowerCase(),
          `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
        ]),
      ),
    [users],
  );
  const uploaderName = (email?: string | null) =>
    email ? nameByEmail[email.toLowerCase()] ?? email : null;

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = products.find((p) => p.id === viewingId) ?? null;
  // Which of the product's images is shown large in the overview gallery —
  // reset whenever a different product is opened.
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const selectProduct = (id: string | null) => {
    setActiveImageIndex(0);
    setViewingId(id);
  };
  const [overviewTab, setOverviewTab] = useState<"details" | "inventory">("details");

  const { data: warehouses = [] } = useGetWarehousesQuery();
  const { data: viewingStockLevels = [] } = useGetStockLevelsQuery(
    viewing ? { productId: viewing.id } : undefined,
    { skip: !viewing },
  );
  const { data: allOrders = [] } = useGetOrdersQuery(undefined, { skip: !viewing });
  const warehouseName = (id?: string | null) =>
    id ? warehouses.find((w) => w.id === id)?.name ?? id : "—";
  const recentOrdersForProduct = useMemo(() => {
    if (!viewing) return [];
    return allOrders
      .filter((o) => o.items?.some((it) => it.productId === viewing.id))
      .slice(0, 5);
  }, [allOrders, viewing]);

  const onGridReady = useCallback((e: GridReadyEvent<Product>) => {
    gridApiRef.current = e.api;
  }, []);

  const onSelectionChanged = useCallback(
    (e: SelectionChangedEvent<Product>) => {
      setSelectedRows(e.api.getSelectedRows());
    },
    [],
  );

  const exportCsv = () =>
    gridApiRef.current?.exportDataAsCsv({ fileName: "products.csv" });

  const clearSelection = () => gridApiRef.current?.deselectAll();

  const columns: ColDef<Product>[] = useMemo(
    () => [
      {
        headerName: "Image",
        field: "images",
        colId: "image",
        minWidth: 70,
        maxWidth: 70,
        sortable: false,
        filter: false,
        cellRenderer: (p: ICellRendererParams<Product>) => {
          const primary = p.data?.images?.find((img) => img.isPrimary) ?? p.data?.images?.[0];
          return primary?.url ? (
            <img
              src={primary.url}
              alt=""
              style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover", display: "block", margin: "4px auto" }}
            />
          ) : (
            <span style={{ display: "grid", placeItems: "center", height: "100%", color: "var(--text-faint)" }}>
              <IconImage size={16} />
            </span>
          );
        },
      },
      { field: "sku", headerName: "SKU", minWidth: 120 },
      { field: "productCode", headerName: "Code", minWidth: 120 },
      {
        field: "productName",
        headerName: "Name",
        minWidth: 200,
        flex: 1,
        cellClass: "ag-cell--link",
      },
      {
        field: "brandId",
        headerName: "Brand",
        minWidth: 130,
        valueGetter: (p: ValueGetterParams<Product>) =>
          p.data?.brandId ? brandName[p.data.brandId] ?? "—" : "—",
      },
      {
        field: "supplierId",
        headerName: "Supplier",
        minWidth: 130,
        valueGetter: (p: ValueGetterParams<Product>) =>
          p.data?.supplierId ? supplierName[p.data.supplierId] ?? "—" : "—",
      },
      {
        field: "categoryId",
        headerName: "Category",
        minWidth: 130,
        valueGetter: (p: ValueGetterParams<Product>) =>
          p.data?.categoryId ? categoryName[p.data.categoryId] ?? "—" : "—",
      },
      {
        field: "sellingPrice",
        headerName: "Selling price",
        type: "numericColumn",
        minWidth: 130,
        valueFormatter: (p) => money(p.value as number, p.data?.currency),
      },
      {
        field: "purchasePrice",
        headerName: "Purchase price",
        type: "numericColumn",
        minWidth: 130,
        valueFormatter: (p) => money(p.value as number, p.data?.currency),
      },
      {
        field: "totalStock",
        headerName: "Stock",
        type: "numericColumn",
        minWidth: 100,
      },
      {
        field: "reorderLevel",
        headerName: "Reorder",
        type: "numericColumn",
        minWidth: 100,
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 140,
        cellRenderer: (p: ICellRendererParams<Product>) => (
          <span className={`badge ${statusTone(p.value as string)}`}>
            <span className="badge__dot" />
            {p.value}
          </span>
        ),
      },
      {
        field: "isFeatured",
        headerName: "Featured",
        minWidth: 100,
        valueFormatter: (p) => (p.value ? "Yes" : "No"),
      },
      {
        headerName: "Actions",
        field: "id",
        colId: "actions",
        width: 70,
        minWidth: 70,
        maxWidth: 70,
        resizable: false,
        pinned: "right",
        sortable: false,
        filter: false,
        cellRenderer: (p: ICellRendererParams<Product>) => {
          const rowId = p.data?.id;
          if (!rowId) return null;
          return (
            // stopPropagation: an ag-grid row is itself clickable, so a menu
            // click must not also select the row behind it.
            <RowMenu
              stopPropagation
              items={[
                {
                  label: "View product",
                  icon: <IconEye size={16} />,
                  onSelect: () => selectProduct(rowId),
                },
                {
                  label: "Edit product",
                  icon: <IconEdit size={16} />,
                  onSelect: () => openDetail(rowId),
                },
                {
                  label: "Delete product",
                  icon: <IconTrash size={16} />,
                  onSelect: () => {
                    if (p.data) handleDeleteOne(p.data);
                  },
                  danger: true,
                },
              ]}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brandName, supplierName, categoryName],
  );

  const defaultColDef = useMemo<ColDef<Product>>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 90,
    }),
    [],
  );

  const rowClassRules = useMemo(
    () => ({
      "ag-row--low-stock": (p: { data?: Product }) => isLowStock(p.data),
    }),
    [],
  );

  const handleDeleteOne = async (product: Product) => {
    if (!window.confirm(`Delete "${product.productName}"?`)) return;
    try {
      await bulkDeleteProducts([product.id]).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete product.",
      );
    }
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} selected product(s)?`))
      return;
    try {
      await bulkDeleteProducts(selected.map((p) => p.id)).unwrap();
      clearSelection();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete products.",
      );
    }
  };

  // Applies every field toggled to "Set to" in one bulkUpdateProducts call —
  // the mutation already accepts a full UpdateProductInput, so multiple
  // fields can be set together (unlike the old one-field-at-a-time bar).
  const handleBulkApply = async () => {
    if (selected.length === 0) return;

    const activeFields = (Object.keys(BULK_FIELDS) as BulkFieldKey[]).filter(
      (f) => bulkEnabled[f],
    );
    if (activeFields.length === 0) {
      window.alert("Choose at least one field to set.");
      return;
    }

    const numericFields: BulkFieldKey[] = ["sellingPrice", "purchasePrice", "reorderLevel"];
    const referenceFields: BulkFieldKey[] = ["categoryId", "brandId", "supplierId"];

    const input: Record<string, unknown> = {};
    for (const field of activeFields) {
      const raw = bulkValues[field] ?? "";
      if (numericFields.includes(field)) {
        if (raw.trim() === "") {
          window.alert(`Enter a value for ${BULK_FIELDS[field].label}.`);
          return;
        }
        input[field] = Number(raw);
      } else if (field === "isFeatured") {
        input[field] = raw === "yes";
      } else if (referenceFields.includes(field) && raw === "") {
        // Reference fields (category/brand/supplier) can be cleared with "".
        input[field] = undefined;
      } else {
        input[field] = raw;
      }
    }

    try {
      await bulkUpdateProducts({
        ids: selected.map((p) => p.id),
        input: input as UpdateProductInput,
      }).unwrap();
      clearSelection();
      setBulkOpen(false);
      setBulkEnabled({});
      setBulkValues({});
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to update products.",
      );
    }
  };

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load products"
    : null;

  // The value control changes shape with the chosen field.
  const renderBulkValueControl = (field: BulkFieldKey) => {
    const value = bulkValues[field] ?? "";
    const setValue = (v: string) => setBulkFieldValue(field, v);
    switch (field) {
      case "status":
        return (
          <select value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">Select…</option>
            {["ACTIVE", "INACTIVE", "DISCONTINUED", "DRAFT"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        );
      case "inventoryTracking":
        return (
          <select value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">Select…</option>
            {["NONE", "QUANTITY", "SERIAL", "BATCH"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        );
      case "currency":
        return (
          <select value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">Select…</option>
            {["INR", "USD", "EUR", "GBP", "AED"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        );
      case "isFeatured":
        return (
          <select value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">Select…</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        );
      case "categoryId":
        return (
          <select value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">— None —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        );
      case "brandId":
        return (
          <select value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">— None —</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        );
      case "supplierId":
        return (
          <select value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">— None —</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Value"
          />
        );
    }
  };

  const bulkBusy = bulkUpdating || bulkDeleting;

  if (viewing) {
    const images = viewing.images ?? [];
    const primaryIndex = Math.max(
      0,
      images.findIndex((img) => img.isPrimary),
    );
    const activeImage = images[activeImageIndex] ?? images[primaryIndex] ?? images[0];
    const margin =
      viewing.sellingPrice && viewing.purchasePrice && viewing.sellingPrice > 0
        ? (((viewing.sellingPrice - viewing.purchasePrice) / viewing.sellingPrice) * 100).toFixed(1)
        : null;
    const dims = viewing.dimensions;
    const dimensionsLabel = dims && (dims.length || dims.width || dims.height)
      ? `${dims.length ?? "—"} × ${dims.width ?? "—"} × ${dims.height ?? "—"} ${dims.dimensionUnit ?? ""}`.trim()
      : "—";

    return (
      <>
        <EntityOverview
          backLabel="Products"
          onBack={() => setViewingId(null)}
          title={viewing.productName}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          subtitle={
            <>
              <span>SKU: {viewing.sku}</span>
              <span>{money(viewing.sellingPrice, viewing.currency)}</span>
              {viewing.barcode ? <span>Barcode: {viewing.barcode}</span> : null}
            </>
          }
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDeleteOne(viewing).then(() => setViewingId(null));
          }}
          side={
            <>
              <div className="overview__image">
                {activeImage?.url ? (
                  <img src={activeImage.url} alt={viewing.productName} />
                ) : (
                  <IconImage size={28} />
                )}
              </div>
              {images.length > 1 ? (
                <div className="overview__thumbs">
                  {images.map((img, index) => (
                    <button
                      key={img.id}
                      type="button"
                      className={`overview__thumb${index === activeImageIndex ? " is-active" : ""}`}
                      aria-label={`Show image ${index + 1} of ${images.length}`}
                      onClick={() => setActiveImageIndex(index)}
                    >
                      {img.thumbnailUrl ?? img.url ? (
                        <img src={img.thumbnailUrl ?? img.url ?? undefined} alt="" />
                      ) : (
                        <IconImage size={14} />
                      )}
                    </button>
                  ))}
                </div>
              ) : null}
              {uploaderName(activeImage?.uploadedBy) ? (
                <p className="overview__image-meta">Uploaded by {uploaderName(activeImage?.uploadedBy)}</p>
              ) : null}
              <div>
                <h3 className="panel__section-title" style={{ marginTop: 0 }}>Recent orders</h3>
                {recentOrdersForProduct.length === 0 ? (
                  <p className="overview__list-empty">No orders reference this product yet.</p>
                ) : (
                  <ul className="overview__list">
                    {recentOrdersForProduct.map((o) => {
                      const qty = o.items?.find((it) => it.productId === viewing.id)?.quantity;
                      return (
                        <li key={o.id} className="overview__list-item">
                          <span>
                            <strong>{o.orderNumber}</strong>
                            <br />
                            {o.orderDate ? new Date(o.orderDate).toLocaleDateString() : "—"}
                          </span>
                          <span>Qty {qty ?? "—"}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          }
        >
          <div className="overview__tabs">
            <button
              type="button"
              className={`overview__tab${overviewTab === "details" ? " is-active" : ""}`}
              onClick={() => setOverviewTab("details")}
            >
              Details
            </button>
            <button
              type="button"
              className={`overview__tab${overviewTab === "inventory" ? " is-active" : ""}`}
              onClick={() => setOverviewTab("inventory")}
            >
              Inventory
            </button>
          </div>

          {overviewTab === "details" ? (
            <OverviewFieldList
              fields={[
                { label: "Description", value: viewing.description, full: true },
                { label: "Category", value: viewing.categoryId ? categoryName[viewing.categoryId] : "—" },
                { label: "Sub-category", value: viewing.subCategoryId ? categoryName[viewing.subCategoryId] : "—" },
                { label: "Brand", value: viewing.brandId ? brandName[viewing.brandId] : "—" },
                { label: "Supplier", value: viewing.supplierId ? supplierName[viewing.supplierId] : "—" },
                { label: "Manufacturer", value: viewing.manufacturer },
                { label: "Unit", value: viewing.unit },
                { label: "Cost price", value: money(viewing.purchasePrice, viewing.currency) },
                { label: "Selling price", value: money(viewing.sellingPrice, viewing.currency) },
                { label: "Margin", value: margin != null ? `${margin}%` : "—" },
                { label: "Tax %", value: viewing.taxPercentage != null ? `${viewing.taxPercentage}%` : "—" },
                { label: "Dimensions", value: dimensionsLabel },
              ]}
            />
          ) : (
            <>
              <OverviewFieldList
                fields={[
                  { label: "Tracking mode", value: viewing.inventoryTracking },
                  { label: "Total stock", value: viewing.totalStock },
                  { label: "Reorder level", value: viewing.reorderLevel },
                  { label: "Maximum stock level", value: viewing.maximumStockLevel },
                ]}
              />
              <h3 className="panel__section-title">Stock by warehouse</h3>
              {viewingStockLevels.length === 0 ? (
                <p className="overview__list-empty">No stock recorded in any warehouse yet.</p>
              ) : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Warehouse</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingStockLevels.map((sl) => (
                        <tr key={sl.id}>
                          <td>{warehouseName(sl.warehouseId)}</td>
                          <td>{sl.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </EntityOverview>
        <ProductFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      {/* Standing state of the whole catalogue, not the filtered view — which
          is why it sits above the search field rather than below it. */}
      <div className="stat-grid">
        <StatCard
          label="Products"
          value={isLoading ? "…" : totals.total}
          tone="primary"
          icon={<IconBox />}
        />
        <StatCard
          label="Inventory value"
          value={isLoading ? "…" : money(totals.value, totals.currency)}
          hint="Stock on hand at cost"
          tone="success"
          icon={<IconChart />}
        />
        <StatCard
          label="Low stock"
          value={isLoading ? "…" : totals.lowStock}
          hint="At or below reorder level"
          tone="warning"
          icon={<IconAlert />}
        />
        <StatCard
          label="Out of stock"
          value={isLoading ? "…" : totals.outOfStock}
          hint="Nothing on hand"
          tone="danger"
          icon={<IconAlert />}
        />
      </div>

      <section className="panel">
        {errorMessage && (
          <div className="table-error" style={{ padding: 12 }}>
            <span>{errorMessage}</span>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => refetch()}
            >
              Try again
            </button>
          </div>
        )}

        <div className="panel__head">
          <div>
            <h2>Products</h2>
            <p className="panel__sub">
              {selected.length > 0
                ? `${selected.length} selected`
                : `${products.length} product(s)`}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="button" className="btn" onClick={exportCsv}>
              Export CSV
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> Add product
            </button>
          </div>
        </div>

        {selected.length > 0 && (
          <div className="selection-bar">
            <span className="selection-bar__count">{selected.length} selected</span>
            <div className="selection-bar__actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setBulkOpen(true)}
              >
                Bulk editing ({selected.length})
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={handleBulkDelete}
                disabled={bulkBusy}
              >
                <IconTrash /> {bulkDeleting ? "Deleting…" : "Delete selected"}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={clearSelection}
                disabled={bulkBusy}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        <div className="products-grid">
          <AgGridReact<Product>
            theme={gridThemeFor(isDarkMode)}
            rowData={products}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            getRowId={(p) => p.data.id}
            rowSelection={{ mode: "multiRow" }}
            onGridReady={onGridReady}
            onSelectionChanged={onSelectionChanged}
            onRowClicked={(e) => {
              // ag-Grid's row-click detection runs independently of React's
              // event system, so the Actions cell's `stopPropagation()` calls
              // never reach it — guard here instead by checking whether the
              // click originated inside the actions cell's dropdown.
              const target = e.event?.target as HTMLElement | null;
              if (target?.closest(".dropdown")) return;
              if (e.data) selectProduct(e.data.id);
            }}
            rowClassRules={rowClassRules}
            quickFilterText={search}
            pagination
            paginationPageSize={25}
            paginationPageSizeSelector={[25, 50, 100]}
            loading={isLoading || isFetching}
            animateRows
          />
        </div>
      </section>

      <Modal
        open={bulkOpen}
        title={`Bulk editing (${selected.length})`}
        onClose={() => setBulkOpen(false)}
        xwide
        footer={
          <>
            <button type="button" className="btn" onClick={() => setBulkOpen(false)} disabled={bulkUpdating}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleBulkApply}
              disabled={bulkUpdating}
            >
              {bulkUpdating ? "Applying…" : "Apply"}
            </button>
          </>
        }
      >
        <div className="bulk-edit">
          <div className="bulk-edit__tabs">
            {BULK_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`bulk-edit__tab${bulkTab === tab.key ? " is-active" : ""}`}
                onClick={() => setBulkTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="bulk-edit__panel">
            {(Object.keys(BULK_FIELDS) as BulkFieldKey[])
              .filter((field) => BULK_FIELDS[field].tab === bulkTab)
              .map((field) => (
                <div className="bulk-edit__row" key={field}>
                  <span className="bulk-edit__row-label">{BULK_FIELDS[field].label}</span>
                  <select
                    className="bulk-edit__row-mode"
                    value={bulkEnabled[field] ? "set" : "none"}
                    onChange={(e) => setBulkFieldMode(field, e.target.value as "none" | "set")}
                  >
                    <option value="none">No change</option>
                    <option value="set">Set to</option>
                  </select>
                  <div className="bulk-edit__row-value">
                    {bulkEnabled[field] ? renderBulkValueControl(field) : null}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Modal>

      <ProductFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Products;
